/**
 * What the Home page shows, and in what order (#670). Pure functions, so
 * the rules are unit-testable apart from the fetching.
 *
 * Every kind of record becomes one `ActivityItem`, stamped with when it
 * last changed. Overdue items are pinned above everything else; the rest
 * read newest first, in two lenses: what is the viewer's, and everything.
 * Urgency no longer decides the order — a high priority or a critical
 * severity is a badge on the row, coloured the way the other views colour
 * it, beside the same severity bars and status icons the lists use.
 */
import {
  CommentReturn,
  HoaReturn,
  HomeReturn,
  MemberReturn,
  NotificationResponse,
  ReservationReturn,
  ViolationReturn,
  WorkOrderReturn,
} from '../clients/rust-api-client';
import { BadgeColor } from '../billing/enumLabels';
import { notificationTypeLabel } from '../notification/notificationMeta';
import { threadCopy } from '../violation/comments/commentLabels';
import {
  isOverdue,
  normalizeSeverity,
  normalizeStatus as violationStatus,
  shortRef as violationRef,
} from '../violation/linear/violationMeta';
import {
  isOpen as isOpenWorkOrder,
  isPastScheduled,
  normalizeStatus as workOrderStatus,
  priorityAsSeverity,
  shortRef as workOrderRef,
  titleCase,
} from '../workorder/linear/workOrderMeta';

export type ActivityKind =
  | 'violation'
  | 'workOrder'
  | 'comment'
  | 'notification'
  | 'communication'
  | 'reservation'
  | 'home'
  | 'member';

export interface ActivityItem {
  /** `kind:id`, unique across kinds. */
  key: string;
  kind: ActivityKind;
  /** When it last changed, epoch milliseconds. */
  at: number;
  /** What happened: "New", "Updated", "Resolved", "Homeowner replied"… */
  verb: string;
  title: string;
  /** A second line: an address, a recipient, the record a comment is on. */
  detail: string | null;
  hoaName: string | null;
  /** The front-porch page it opens. */
  path: string;
  overdue: boolean;
  /** Priority or severity worth a badge — "Urgent", "Critical" in red; "High", "Major" in orange. */
  badge: { label: string; color: 'red' | 'orange' } | null;
  /** On the violation severity scale (a work order's priority mapped onto it), for the bar glyph. */
  severity: string | null;
  /** The record's own status, for its coloured status icon. */
  status: string | null;
  /** A comment's thread, labelled and coloured as the thread itself shows it. */
  thread: { label: string; color: BadgeColor } | null;
  /** An unread staff notification. */
  unread: boolean;
  /** The viewer's own: assigned to them, addressed to them, waiting on them. */
  mine: boolean;
}

/** Everything Home reads; a kind it cannot read is an empty list. */
export interface HomeSources {
  violations: ViolationReturn[];
  workOrders: WorkOrderReturn[];
  comments: CommentReturn[];
  /** Staff notifications (`is_for_staff`). */
  notifications: NotificationResponse[];
  /** What went to homeowners (`is_for_member`); empty for a board member. */
  communications: NotificationResponse[];
  reservations: ReservationReturn[];
  homes: HomeReturn[];
  /** Each member with an association they have a home in. */
  members: { member: MemberReturn; hoaId: string }[];
  hoas: HoaReturn[];
}

export const EMPTY_SOURCES: HomeSources = {
  violations: [],
  workOrders: [],
  comments: [],
  notifications: [],
  communications: [],
  reservations: [],
  homes: [],
  members: [],
  hoas: [],
};

export interface Viewer {
  userId: string | null;
  fullName: string | null;
  email: string | null;
  /** May approve or deny a reservation request. */
  decidesReservations: boolean;
}

export interface HomeFeed {
  /** Pinned: open and past due, newest activity first. */
  overdue: ActivityItem[];
  /** The viewer's own, newest first; overdue ones are already pinned. */
  forYou: ActivityItem[];
  /** Everything, newest first; overdue ones are already pinned. */
  recent: ActivityItem[];
}

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------
// Violations, work orders, comments and notifications stamp ISO strings;
// homes, members and reservations stamp epoch seconds.

export const isoMillis = (value: string | null | undefined): number => {
  if (!value) return 0;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
};

export const secondsMillis = (value: number | null | undefined): number =>
  typeof value === 'number' && value > 0 ? value * 1000 : 0;

/**
 * "New" until something changed after the record was made. A save in the
 * same second as the create (the create's own follow-up writes) is not an
 * update.
 */
const createdOrUpdated = (created: number, updated: number): string =>
  updated > created + 1000 ? 'Updated' : 'New';

// ---------------------------------------------------------------------------
// One item per record
// ---------------------------------------------------------------------------

interface Lookups {
  hoas: ReadonlyMap<string, HoaReturn>;
  homes: ReadonlyMap<string, HomeReturn>;
  violations: ReadonlyMap<string, ViolationReturn>;
  workOrders: ReadonlyMap<string, WorkOrderReturn>;
}

const hoaNameOf = (l: Lookups, hoaId: string | null | undefined): string | null =>
  (hoaId && l.hoas.get(hoaId)?.name) || null;

const addressOf = (home: HomeReturn | undefined): string | null =>
  home ? [home.street, home.street2].filter(Boolean).join(' ') : null;

/**
 * Red at the top of the scale and orange one below it — the steps where
 * the bar glyph fills — and nothing for the rest.
 */
const urgencyBadge = (scale: string, label: string): ActivityItem['badge'] =>
  scale === 'Critical'
    ? { label, color: 'red' }
    : scale === 'Major'
      ? { label, color: 'orange' }
      : null;

const sameText = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * `assigned_to` is free text typed on the work order, not a user id, so it
 * is the viewer's when it reads as their id, name or email.
 */
export const isAssignedTo = (w: WorkOrderReturn, viewer: Viewer): boolean =>
  sameText(w.assigned_to, viewer.userId) ||
  sameText(w.assigned_to, viewer.fullName) ||
  sameText(w.assigned_to, viewer.email);

export const violationItem = (
  v: ViolationReturn,
  l: Lookups,
  now: Date
): ActivityItem => {
  const created = isoMillis(v.date_created) || isoMillis(v.date_of_violation);
  const at = Math.max(created, isoMillis(v.date_updated));
  const severity = normalizeSeverity(v.severity);
  const home = l.homes.get(v.home_id);
  return {
    key: `violation:${v.id}`,
    kind: 'violation',
    at,
    verb:
      violationStatus(v.status) === 'Resolved'
        ? 'Resolved'
        : createdOrUpdated(created, at),
    title: v.description || titleCase(v.violation_type),
    detail: [violationRef(v.id), addressOf(home)].filter(Boolean).join(' · '),
    hoaName: hoaNameOf(l, home?.hoa_id),
    path: `/violations/${v.id}`,
    overdue: isOverdue(v, now),
    badge: urgencyBadge(severity, severity),
    severity,
    status: v.status,
    thread: null,
    unread: false,
    // No assignee to match on: violations are everyone's (#670).
    mine: false,
  };
};

export const workOrderItem = (
  w: WorkOrderReturn,
  l: Lookups,
  viewer: Viewer,
  now: Date
): ActivityItem => {
  const created = isoMillis(w.date_created);
  const at = Math.max(created, isoMillis(w.date_updated));
  const status = workOrderStatus(w.status);
  const open = isOpenWorkOrder(w);
  const scale = priorityAsSeverity(w.priority);
  return {
    key: `workOrder:${w.id}`,
    kind: 'workOrder',
    at,
    verb:
      status === 'Completed' || status === 'Cancelled'
        ? status
        : createdOrUpdated(created, at),
    title: w.title,
    detail:
      [workOrderRef(w.id), addressOf(w.home_id ? l.homes.get(w.home_id) : undefined), w.vendor_name]
        .filter(Boolean)
        .join(' · ') || null,
    hoaName: hoaNameOf(l, w.hoa_id),
    path: `/work-orders/${w.id}`,
    overdue: isPastScheduled(w, now),
    badge: urgencyBadge(scale, titleCase(w.priority)),
    severity: scale || null,
    status: w.status,
    thread: null,
    unread: false,
    mine:
      open &&
      (isAssignedTo(w, viewer) || (!!viewer.userId && w.created_by === viewer.userId)),
  };
};

/**
 * A comment on a violation or work order. The viewer's when someone else
 * wrote it on a record the viewer made or has commented on. `null` for
 * one whose record is not in view.
 */
export const commentItem = (
  c: CommentReturn,
  l: Lookups,
  viewer: Viewer,
  involved: ReadonlySet<string>
): ActivityItem | null => {
  const violation = c.violation_id ? l.violations.get(c.violation_id) : undefined;
  const workOrder = c.work_order_id ? l.workOrders.get(c.work_order_id) : undefined;
  if (!violation && !workOrder) return null;
  const parentPath = violation
    ? `/violations/${violation.id}`
    : `/work-orders/${workOrder!.id}`;
  const parentLabel = violation
    ? `${violationRef(violation.id)} · ${violation.description}`
    : `${workOrderRef(workOrder!.id)} · ${workOrder!.title}`;
  const hoaId = violation ? l.homes.get(violation.home_id)?.hoa_id : workOrder!.hoa_id;
  const parentId = (violation ?? workOrder)!.id;
  const fromHomeowner = c.author_realm === 'member';
  // The thread's own label and colour ("Internal" amber, "Shared with …" sky).
  const thread = c.visibility
    ? threadCopy(c.visibility, violation ? 'violation' : 'work_order')
    : null;
  const ownedByViewer =
    !!viewer.userId && (violation ?? workOrder)!.created_by === viewer.userId;
  return {
    key: `comment:${c.id}`,
    kind: 'comment',
    at: isoMillis(c.created_at),
    verb: fromHomeowner ? 'Homeowner replied' : 'Comment',
    title: c.content,
    detail: parentLabel,
    hoaName: hoaNameOf(l, hoaId),
    path: `${parentPath}?comment=${encodeURIComponent(c.id)}`,
    overdue: false,
    badge: null,
    severity: null,
    status: null,
    thread: thread && { label: thread.label, color: thread.badgeColor },
    unread: false,
    mine:
      c.author_id !== viewer.userId && (ownedByViewer || involved.has(parentId)),
  };
};

export const notificationItem = (
  n: NotificationResponse,
  l: Lookups,
  viewer: Viewer
): ActivityItem => ({
  key: `notification:${n.id}`,
  kind: 'notification',
  at: isoMillis(n.created_at),
  verb: notificationTypeLabel(n.notification_type),
  title: n.title,
  detail: n.message || null,
  hoaName: hoaNameOf(l, n.hoa_id) ?? (n.hoa_name || null),
  path: `/notifications/${n.id}`,
  overdue: false,
  badge: null,
  severity: null,
  status: null,
  thread: null,
  unread: !n.is_read,
  // A staff notification's `user_id` is the user it was addressed to.
  mine: !!viewer.userId && n.user_id === viewer.userId,
});

export const communicationItem = (
  n: NotificationResponse,
  l: Lookups
): ActivityItem => ({
  key: `communication:${n.id}`,
  kind: 'communication',
  at: isoMillis(n.created_at),
  verb: 'Sent',
  title: n.title,
  detail: n.recipient_name ? `To ${n.recipient_name}` : null,
  hoaName: hoaNameOf(l, n.hoa_id) ?? (n.hoa_name || null),
  path: `/communications/${n.id}`,
  overdue: false,
  badge: null,
  severity: null,
  status: null,
  thread: null,
  unread: false,
  mine: false,
});

const RESERVATION_VERBS: Record<string, string> = {
  pending: 'Requested',
  approved: 'Approved',
  denied: 'Denied',
  cancelled: 'Cancelled',
  expired: 'Expired',
  completed: 'Completed',
  no_show: 'No-show',
};

export const reservationItem = (
  r: ReservationReturn,
  l: Lookups,
  viewer: Viewer
): ActivityItem => ({
  key: `reservation:${r.id}`,
  kind: 'reservation',
  at: Math.max(
    secondsMillis(r.date_created),
    secondsMillis(r.decided_at),
    secondsMillis(r.cancelled_at),
    secondsMillis(r.checked_in_at),
    secondsMillis(r.checked_out_at)
  ),
  verb: RESERVATION_VERBS[r.status] ?? titleCase(r.status),
  title: `${r.amenity_name || 'Amenity'} · ${r.local_date}`,
  detail: [r.home_address, r.booked_by_name].filter(Boolean).join(' · ') || null,
  hoaName: hoaNameOf(l, r.hoa_id),
  path: `/reservations/${r.id}`,
  overdue: false,
  badge: null,
  severity: null,
  status: r.status,
  thread: null,
  unread: false,
  // A request waits on whoever may decide it.
  mine: r.status === 'pending' && viewer.decidesReservations,
});

/** A home appears once, when it is added; edits to an address are not news. */
export const homeItem = (h: HomeReturn, l: Lookups): ActivityItem | null => {
  const at = secondsMillis(h.date_created);
  if (!at) return null;
  return {
    key: `home:${h.id}`,
    kind: 'home',
    at,
    verb: 'New home',
    title: addressOf(h) ?? h.street,
    detail: [h.city, h.state].filter(Boolean).join(', ') || null,
    hoaName: hoaNameOf(l, h.hoa_id),
    path: `/hoa/${h.hoa_id}/home/${h.id}`,
    overdue: false,
    badge: null,
    severity: null,
    status: null,
    thread: null,
    unread: false,
    mine: false,
  };
};

/**
 * A member appears once, when they are added. Their `updated_at` moves for
 * things that are not news on Home (signing in to the portal, a phone
 * number), so it is not read.
 */
export const memberItem = (
  m: MemberReturn,
  hoaId: string,
  l: Lookups
): ActivityItem | null => {
  const at = secondsMillis(m.created_at);
  if (!at) return null;
  return {
    key: `member:${m.id}:${hoaId}`,
    kind: 'member',
    at,
    verb: 'New member',
    title: m.display_name || m.full_name,
    detail: m.email || null,
    hoaName: hoaNameOf(l, hoaId),
    path: `/hoa/${hoaId}/member/${m.id}`,
    overdue: false,
    badge: null,
    severity: null,
    status: null,
    thread: null,
    unread: false,
    mine: false,
  };
};

// ---------------------------------------------------------------------------
// The feed
// ---------------------------------------------------------------------------

/** Newest first; ties broken by key so the order is stable. */
export const newestFirst = (a: ActivityItem, b: ActivityItem): number =>
  b.at - a.at || a.key.localeCompare(b.key);

const byId = <T extends { id: string }>(rows: T[]): Map<string, T> =>
  new Map(rows.map((r) => [r.id, r]));

export const buildHomeFeed = (
  src: HomeSources,
  viewer: Viewer,
  now: Date = new Date()
): HomeFeed => {
  const l: Lookups = {
    hoas: byId(src.hoas),
    homes: byId(src.homes),
    violations: byId(src.violations),
    workOrders: byId(src.workOrders),
  };
  const comments = src.comments.filter((c) => !c.is_deleted);
  // Records the viewer has written on, so replies there are theirs to see.
  const involved = new Set(
    comments
      .filter((c) => viewer.userId && c.author_id === viewer.userId)
      .map((c) => c.violation_id ?? c.work_order_id)
      .filter((id): id is string => !!id)
  );

  const items: ActivityItem[] = [
    ...src.violations.map((v) => violationItem(v, l, now)),
    ...src.workOrders.map((w) => workOrderItem(w, l, viewer, now)),
    ...comments
      .map((c) => commentItem(c, l, viewer, involved))
      .filter((i): i is ActivityItem => i !== null),
    ...src.notifications.map((n) => notificationItem(n, l, viewer)),
    ...src.communications.map((n) => communicationItem(n, l)),
    ...src.reservations.map((r) => reservationItem(r, l, viewer)),
    ...src.homes
      .map((h) => homeItem(h, l))
      .filter((i): i is ActivityItem => i !== null),
    ...src.members
      .map(({ member, hoaId }) => memberItem(member, hoaId, l))
      .filter((i): i is ActivityItem => i !== null),
  ].sort(newestFirst);

  return {
    overdue: items.filter((i) => i.overdue),
    forYou: items.filter((i) => i.mine && !i.overdue),
    recent: items.filter((i) => !i.overdue),
  };
};

/**
 * The associations whose money Home may ask about (#670). A Manager's
 * copy lists every association in the company — `GET /hoa` does — but
 * holds homes, work orders and bookings only for the ones they are
 * assigned to, which is exactly where the money endpoints let them in
 * (back-end-rs/docs/SYNC_ENGINE.md §5). Asking for the rest only earns
 * 403s. Everyone else may ask about every association they can see.
 */
export const moneyHoaIds = (src: HomeSources, isManager: boolean): string[] => {
  if (!isManager) return src.hoas.map((h) => h.id);
  const assigned = new Set<string>([
    ...src.homes.map((h) => h.hoa_id),
    ...src.workOrders.map((w) => w.hoa_id),
    ...src.reservations.map((r) => r.hoa_id),
  ]);
  return src.hoas.map((h) => h.id).filter((id) => assigned.has(id));
};
